import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider } from '@vis.gl/react-google-maps'
import AddressSearchBar from './components/AddressSearchBar'
import GoogleMapContainer from './components/GoogleMapContainer'
import { buildAddressFieldsFromPlace } from './utils/parsePolishAddress'

const googleMapsApiKey = 'AIzaSyCaMtih5UqeAP7MAGclyi2xGaPLIhFc9QA'

const inputClassName = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'

const toCoordinate = value => {
	if (value === '' || value == null) return null
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : null
}

const buildPlacowkaAddressSearchValue = placowka => {
	const adres = `${placowka?.adres || ''}`.trim()
	const miasto = `${placowka?.id_miasto?.nazwa || placowka?.miasto || ''}`.trim()
	return [adres, miasto].filter(Boolean).join(', ')
}

export default function ProjectStepLocationSection({
	form,
	onChange,
	selectedPlacowka = null,
}) {
	const { t } = useTranslation()
	const [searchRequest, setSearchRequest] = useState(null)
	const [isResolvingPlacowkaAddress, setIsResolvingPlacowkaAddress] = useState(false)

	const g_lat = useMemo(() => toCoordinate(form.g_lat), [form.g_lat])
	const g_long = useMemo(() => toCoordinate(form.g_long), [form.g_long])
	const placowkaSearchValue = useMemo(
		() => buildPlacowkaAddressSearchValue(selectedPlacowka),
		[selectedPlacowka]
	)

	const handlePlaceSelect = useCallback(place => {
		const fields = buildAddressFieldsFromPlace(place)
		if (!fields) return
		onChange(fields)
	}, [onChange])

	const handlePositionChange = useCallback((nextLat, nextLong) => {
		onChange({
			g_lat: nextLat,
			g_long: nextLong,
		})
	}, [onChange])

	const handleUseFromPlacowka = () => {
		if (!placowkaSearchValue || isResolvingPlacowkaAddress) return
		setIsResolvingPlacowkaAddress(true)
		setSearchRequest(prev => ({
			value: placowkaSearchValue,
			nonce: (prev?.nonce || 0) + 1,
		}))
	}

	const handleSearchRequestComplete = useCallback(() => {
		setIsResolvingPlacowkaAddress(false)
	}, [])

	const addressFields = (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
			<label className="flex flex-col gap-1 md:col-span-2">
				<span className="text-sm font-medium text-gray-700">{t('trainingTrainers.form.fields.address')}</span>
				<input
					type="text"
					value={form.ulica || ''}
					onChange={event => onChange({ ulica: event.target.value })}
					placeholder={t('trainingTrainers.form.placeholders.address')}
					className={inputClassName}
				/>
			</label>

			<label className="flex flex-col gap-1">
				<span className="text-sm font-medium text-gray-700">{t('trainingTrainers.form.fields.city')}</span>
				<input
					type="text"
					value={form.miasto || ''}
					onChange={event => onChange({ miasto: event.target.value })}
					placeholder={t('trainingTrainers.form.placeholders.city')}
					className={inputClassName}
				/>
			</label>

			<label className="flex flex-col gap-1">
				<span className="text-sm font-medium text-gray-700">{t('trainingTrainers.form.fields.postalCode')}</span>
				<input
					type="text"
					value={form.kod_pocztowy || ''}
					onChange={event => onChange({ kod_pocztowy: event.target.value })}
					placeholder={t('trainingTrainers.form.placeholders.postalCode')}
					className={inputClassName}
				/>
			</label>
		</div>
	)

	const mapsContent = googleMapsApiKey ? (
		<APIProvider apiKey={googleMapsApiKey} version="beta" libraries={['places', 'marker']}>
			<div>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.trainerModule.addressSearchLabel')}</span>
					<button
						type="button"
						onClick={handleUseFromPlacowka}
						disabled={!placowkaSearchValue || isResolvingPlacowkaAddress}
						className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isResolvingPlacowkaAddress
							? t('projects.form.locationSection.useFromPlacowkaLoading', {
								defaultValue: 'Szukam adresu...',
							})
							: t('projects.form.locationSection.useFromPlacowka', {
								defaultValue: 'Użyj z placówki',
							})}
					</button>
				</div>
				<div className="mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500">
					<AddressSearchBar
						placeholder={t('settingsPage.users.trainerModule.addressSearchPlaceholder')}
						onPlaceSelect={handlePlaceSelect}
						searchRequest={searchRequest}
						onSearchRequestComplete={handleSearchRequestComplete}
					/>
				</div>
			</div>

			<div className="mt-4 space-y-4">
				{addressFields}

				{g_lat != null && g_long != null ? (
					<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
						<GoogleMapContainer
							g_placeid={form.g_placeid}
							g_lat={g_lat}
							g_long={g_long}
							onPositionChange={handlePositionChange}
						/>
						<p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 text-center">
							g_lat: {g_lat}, g_long: {g_long}
							{form.g_placeid ? (
								<>
									<br />
									g_placeid: {form.g_placeid}
								</>
							) : null}
						</p>
					</div>
				) : null}
			</div>
		</APIProvider>
	) : (
		<div className="mt-4">{addressFields}</div>
	)

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<h3 className="text-sm font-semibold text-gray-900">{t('projects.form.fields.location')}</h3>
			<p className="mt-1 text-sm text-gray-500">{t('projects.form.locationSection.help')}</p>

			<div className="mt-4">
				{mapsContent}
			</div>
		</div>
	)
}
